import type {Observable} from 'rxjs';
import {BehaviorSubject, Subject} from 'rxjs';
import type {ScannerFactory} from './barcode-scanner.types';
import {ScannerState} from './barcode-scanner.types';
import type {IPromise, ITimeoutService, IWindowService} from 'angular';
import type {Barcode} from './barcode';
import type {BarcodeScannerViewerComponentController} from './kp-barcode-scanner-viewer/kp-barcode-scanner-viewer.component';
import type ToasterService from '../commons/services/toaster-service';
import {addSubscriptionHooks} from 'shared/utils/observables-utils';
import type {StreamBarcodeScanner} from './stream-barcode-scanner';
import bind from 'bind-decorator';

export class BarcodeScannerService {

    public static readonly serviceName = 'barcodeScannerService';

    private scannerEnabledByUser: boolean;
    private readonly scannerEnabledGlobally: boolean;

    private scannerViewerReady: boolean;
    private scannerActive: boolean;
    private scannerViewerDisplayed: boolean;

    private barcodeScanner: StreamBarcodeScanner;
    private scannerViewerComponent: BarcodeScannerViewerComponentController;

    private readonly scannerValue$: Subject<Barcode>; // stream of values from scanner (source)
    private readonly scannedBarcode$: Observable<Barcode>; // stream with automatic camera control, output to service subscribers
    private readonly scannerEnabledState$: Subject<boolean>
    private deferActivationUntilViewerReady: boolean;
    private deferOpeningUntilViewerReady: boolean;

    private scannerDeactivationPromise: IPromise<void> = null;

    private barcodeConsumersCount = 0;

    private readonly SCANNER_DEACTIVATION_TIMEOUT = 10000;
    private readonly SCANNER_ACTIVATION_ERROR_MSG = 'Scanner activation error';
    private readonly BARCODE_CONSUMER_MISSING_MSG = 'No barcode consumer to unregister';
    private readonly VIEWER_COMPONENT_MISSING_MSG = 'Scanner Viewer is not registered! ' +
        'BarcodeScannerViewerComponent is probably missing from local or global scope. ' +
        'Please add missing component.';

    /*@ngInject*/
    constructor(
        private $window: IWindowService,
        private $timeout: ITimeoutService,
        private zxingJsBarcodeScannerFactory: ScannerFactory,
        CAMERA_SCANNER_ENABLED: boolean,
        private toasterService: ToasterService,
    ) {

        this.scannerViewerReady = false;
        this.deferActivationUntilViewerReady = false;
        this.deferOpeningUntilViewerReady = false;
        this.scannerViewerDisplayed = false;

        this.scannerEnabledGlobally = CAMERA_SCANNER_ENABLED;
        this.scannerEnabledByUser = false;
        // this.scannerEnabledByUser = this.$window.localStorage.getItem(BarcodeScannerService.localStorageKey) === 'true';

        this.scannerEnabledState$ = new BehaviorSubject<boolean>(this.scannerEnabledByUser);

        this.scannerValue$ = new Subject<Barcode>();
        this.scannedBarcode$ = this.scannerValue$
            .pipe(addSubscriptionHooks(this.onScannedBarcode$Subscribe, this.onScannedBarcode$Unsubscribe));
    }

    private get isScannerEnabledByUser(): boolean {
        return this.scannerEnabledByUser;
    }

    private set isScannerEnabledByUser(value: boolean) {

        if (value === this.scannerEnabledByUser) { // do nothing if set to current value
            return;
        }

        this.scannerEnabledByUser = value;
        this.scannerEnabledState$.next(value);

        if (!this.isGloballyAllowed()) { // ignore if scanner is globally disabled
            return;
        }

        if (value && !this.scannerActive) { // start or stop scanning
            if (this.scannerViewerReady) {
                this.activateScanner(); // activate immediately
            } else {
                this.deferActivationUntilViewerReady = true; // defer activation until viewer is registered
            }
        }
    }

    public getScannerEnabledState$(): Observable<boolean> {
        return this.scannerEnabledState$.asObservable();
    }

    public toggleScanning() {
        if (this.isScannerEnabledByUser) {
            this.stopScanning();
        } else {
            this.startScanning();
        }
    }

    public startScanning() {
        this.isScannerEnabledByUser = true;
        if (!!this.barcodeScanner) { // scanner could be not yet initialized
            this.barcodeScanner.clearValidationBuffer();
        }
    }

    public stopScanning() {
        this.isScannerEnabledByUser = false;
    }

    public isOperating(): boolean {
        if (!this.scannerViewerReady) {
            return false;
        }
        return this.barcodeScanner.getState() === ScannerState.OPERATING;
    }

    public isInitializing(): boolean {
        if (!this.scannerViewerReady) {
            return false;
        }
        return this.barcodeScanner.getState() === ScannerState.STARTING;
    }

    public isAllowed(): boolean {
        return this.isGloballyAllowed() && this.isScannerEnabledByUser;
    }

    public isGloballyAllowed(): boolean {
        return this.scannerEnabledGlobally;
    }

    public isUsable(): boolean {
        return this.isGloballyAllowed() && this.isSupported();
    }

    // register barcode scanner directive
    public registerBarcodeScannerConsumer(): void {

        this.barcodeConsumersCount++;

        if (!this.isAllowed() || this.scannerActive) { // ignore if already activated or not allowed at all
            return;
        }

        if (this.scannerViewerReady) {
            this.activateScanner(); // activate immediately
        } else {
            this.deferActivationUntilViewerReady = true; // defer activation until viewer is registered
        }
    }

    // unregister barcode scanner directive
    public unregisterBarcodeScannerConsumer(): void {

        if (this.barcodeConsumersCount === 0) {
            throw new Error(this.BARCODE_CONSUMER_MISSING_MSG);
        }

        this.barcodeConsumersCount--;

        if (this.barcodeConsumersCount !== 0) {
            return;
        }

        if (this.scannerViewerReady) {
            this.closeScanner();
        } else {
            this.deferActivationUntilViewerReady = false; // cancel deferred activation
        }
    }

    public getBarcodeScannerConsumersCount(): number {
        return this.barcodeConsumersCount;
    }

    public isSupported(): boolean {
        const mediaDevices = this.$window.navigator.mediaDevices;
        return mediaDevices && typeof mediaDevices.getUserMedia === 'function';
    }

    public getScannedBarcode$(): Observable<Barcode> {
        return this.scannedBarcode$;
    }

    public registerScannerViewer(scannerViewerComponent: BarcodeScannerViewerComponentController) {

        this.scannerViewerComponent = scannerViewerComponent

        this.barcodeScanner =
            this.zxingJsBarcodeScannerFactory.createScanner(this.scannerViewerComponent.getVideoElement());

        this.scannerViewerReady = true;

        if (this.deferActivationUntilViewerReady) { // deferred activation
            this.activateScanner();
        }

        if (this.deferOpeningUntilViewerReady) { // deferred opening
            this.openViewer();
        }
    }

    public unregisterScannerViewer() {
        this.deferActivationUntilViewerReady = false;
        this.deferOpeningUntilViewerReady = false;
        this.closeScanner();
        this.scannerViewerReady = false;
        this.barcodeScanner = null;
        this.scannerViewerComponent = null;
    }

    public isScannerViewerDisplayed(): boolean {
        return this.scannerViewerDisplayed;
    }

    // start scanning
    private activateScanner() {

        if (!this.scannerViewerReady) {
            throw new Error(this.VIEWER_COMPONENT_MISSING_MSG)
        }

        this.scannerActive = true;

        // split subscription chain in order to reuse one subject
        const barcode$ = this.barcodeScanner.scan();
        barcode$.subscribe(
            (barcode) => this.onScannerValue(barcode),
            (error) => this.onScannerError(error));
    }

    // stop scanning
    private deactivateScanner() {

        this.cancelScannerDeactivationTimeout();

        if (!this.scannerViewerReady) {
            throw new Error(this.VIEWER_COMPONENT_MISSING_MSG)
        }

        this.barcodeScanner.stopScanning();
        this.scannerActive = false;
    }

    private onScannerValue(barcode: Barcode) {
        if (this.isScannerEnabledByUser) {
            this.scannerValue$.next(barcode);
        }
    }

    private onScannerError(error) {
        this.toasterService.showError(error.message, error.name ?? this.SCANNER_ACTIVATION_ERROR_MSG);
        this.closeScanner();
    }

    @bind
    private onScannedBarcode$Subscribe() {
        if (!this.isScannerEnabledByUser) {
            return;
        }
        if (this.scannerViewerReady) {
            this.openViewer();
        } else {
            this.deferOpeningUntilViewerReady = true; // defer opening until viewer is registered
        }
    }

    @bind
    private onScannedBarcode$Unsubscribe() {

        if (this.scannerViewerReady) {
            this.closeViewer();
        } else {
            this.deferOpeningUntilViewerReady = false; // cancel deferred opening
        }
    }

    private openViewer() {

        if (this.scannerViewerDisplayed)
            return;

        this.cancelScannerDeactivationTimeout();
        this.scannerViewerComponent.openViewer();
        this.scannerViewerDisplayed = true;
    }

    private closeViewer() {

        if (!this.scannerViewerDisplayed)
            return;

        this.scannerViewerComponent.closeViewer();
        this.scannerViewerDisplayed = false;
        this.startScannerDeactivationTimeout();
    }

    @bind
    private closeScanner() {
        this.stopScanning();
        this.deactivateScanner();
    }

    private startScannerDeactivationTimeout() {
        this.scannerDeactivationPromise = this.$timeout(this.closeScanner, this.SCANNER_DEACTIVATION_TIMEOUT);
    }

    private cancelScannerDeactivationTimeout() {

        if (this.scannerDeactivationPromise !== null) {
            this.$timeout.cancel(this.scannerDeactivationPromise);
            this.scannerDeactivationPromise = null;
        }
    }
}